import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../../../components/card/card";
import PlanCalendar from "./calendar";
import { Button, Modal, Spin, Tabs, Dropdown } from "antd";
import type { TabsProps } from "antd";
import { Icon } from "../../../../components/icon/icon";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import TagsSelect from "../../../../components/tags-select/tags-select";
import ApiPlanManagement from "../../../../api/plan-management";
import { LoadingOutlined } from "@ant-design/icons";
import TriggeredTasks from "./triggered-tasks";
import PlanDefaultDataModal from "./plan-default-data-modal";
import ChangeLogsModalComponent from "../../../../components/modals/change-logs/change-logs-modal";

import { FormSelect } from "../../../../components/form/form";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SELECT_COMPLETION_OPTIONS = [
  { key: "reStart", value: "Re-start" },
  { key: "repeatLastWeek", value: "Repeat last week " },
];

const EMPTY_DEFAULT_DATA = {
  scheduleDefaults: {},
  notificationDefaults: {},
};

const DiscardModalComponent = ({
  title,
  handleOk,
  handleCancel,
  isModalOpen,
  confirmLoading,
}) => {
  return (
    <Modal
      title={title}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      okText="Discard changes"
      className="plan-page-action-modal"
      confirmLoading={confirmLoading}
    >
      <p>Are you sure you want to discard changes?</p>
    </Modal>
  );
};

const RemoveModalComponent = ({
  isModalOpen,
  handleOk,
  handleCancel,
  title,
}) => (
  <Modal
    title={title}
    open={isModalOpen}
    onOk={handleOk}
    onCancel={handleCancel}
    okButtonProps={{ danger: true }}
    okText="Remove"
    cancelText="Cancel"
    className="plan-page-action-modal"
  >
    <p>All items mapped to the removed week will disappear from the plan</p>
  </Modal>
);

const normalizeGroups = (group = []) => {
  return group.map((cohort) => ({
    id: cohort.id,
    label: cohort.EnrollmentFieldOption.value,
  }));
};

const MAX_WIDTH = 360;
const MIN_WIDTH = 150;
const TITLE_FONT = "700 16px Sora";

const normalizeDefaultData = (value) => ({
  scheduleDefaults: value?.scheduleDefaults || {},
  notificationDefaults: value?.notificationDefaults || {},
});

const PlanPage = ({
  plan,
  cohorts,
  scenarios,
  changeLogs,
  toList,
  refreshPlan,
  openNotification,
}) => {
  const { t } = useTranslation();
  const { title, id } = plan || {};

  const [completionLogic, setCompletionLogic] = useState("repeatLastWeek");
  const [planTasks, setPlanTasks] = useState([]);
  const [planScenarios, setPlanScenarios] = useState([]);
  const [versionStatus, setVersionStatus] = useState(null);
  const [planActiveCohortsIds, setPlanActiveCohortsIds] = useState([]);
  const [isOpenDiscardModal, setOpenDiscardModal] = useState();
  const [isOpenChangeLogsModal, setOpenChangeLogsModal] = useState();
  const [cohortsKey, setCohortsKey] = useState(0);

  const [isLoading, setLoading] = useState(true);
  const [inProcess, setProcess] = useState(false);
  const [activeWeek, setActiveWeek] = useState(0);
  const [weeklySchedules, setWeeklySchedules] = useState([]);
  const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);
  const [weekToRemove, setWeekToRemove] = useState(null);

  const [triggeredTasks, setTriggeredTasks] = useState([]);
  const logsForPlan = (changeLogs && changeLogs[plan?.id]) || [];

  const [titleValue, setTitleValue] = useState(title);
  const titleRef = useRef(null);
  const activatingRef = useRef(false);
  const discardingRef = useRef(false);
  const cohortSaveRef = useRef(Promise.resolve());
  const [discarding, setDiscarding] = useState(false);
  const [titleWidth, setTitleWidth] = useState(MIN_WIDTH);
  const [previewActive, setPreviewActive] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null);
  const sourcePlan = previewActive && activeVersion ? activeVersion : plan;
  const [readOnly, setReadOnly] = useState(false);

  const [allPlanTasks, setAllPlanTasks] = useState([]);
  const [allPlanScenarios, setAllPlanScenarios] = useState([]);

  const [isDefaultDataModalOpen, setDefaultDataModalOpen] = useState(false);
  const [planDefaultData, setPlanDefaultData] = useState(EMPTY_DEFAULT_DATA);

  const updateWidth = () => {
    requestAnimationFrame(() => {
      const canvas = updateWidth.canvas || document.createElement("canvas");
      updateWidth.canvas = canvas;
      const context = canvas.getContext("2d");
      context.font = TITLE_FONT;

      const measuredWidth = context.measureText(titleValue || "").width + 24;
      const newWidth = Math.min(
        Math.max(Math.ceil(measuredWidth), MIN_WIDTH),
        MAX_WIDTH
      );

      setTitleWidth(newWidth);
    });
  };

  const handlePreviewActive = async () => {
    try {
      if (!activeVersion) {
        const resp = await ApiPlanManagement.getPlanById(plan.id, {
          status: "active",
        });
        const activeV = resp?.data?.plan || null;
        setActiveVersion(activeV);
      }
      setPreviewActive(true);
      setReadOnly(true);
      closeChangeLogsModal();
    } finally {
    }
  };

  const handleReturnToDraft = () => {
    setPreviewActive(false);
    setReadOnly(false);
  };

  useEffect(() => {
    setTimeout(updateWidth, 0);
  }, []);

  useEffect(() => {
    updateWidth();
  }, [titleValue]);

  useEffect(() => {
    setTitleValue(title || "");
  }, [title]);

  useEffect(() => {
    if (weeklySchedules.length > 0) {
      setCompletionLogic(
        weeklySchedules[activeWeek]?.completionLogic || "reStart"
      );
    }
  }, [weeklySchedules, activeWeek]);

  useEffect(() => {
    setCohortsKey((k) => k + 1);
  }, [
    previewActive,
    sourcePlan?.id,
    JSON.stringify(weeklySchedules),
    JSON.stringify(planActiveCohortsIds),
  ]);

  useEffect(() => {
    const source = previewActive && activeVersion ? activeVersion : plan;
    setPlanDefaultData(normalizeDefaultData(source?.defaultData));
  }, [plan, activeVersion, previewActive]);

  const updateCompletionLogic = (value) => {
    setCompletionLogic(value);
    ApiPlanManagement.updatePlanLogic(plan.id, { completionLogic: value })
      .then(() => {
        refreshPlan();
      })
      .catch((error) => {
        console.error("Error updating completion logic:", error);
      });
  };

  useEffect(() => {
    if (!sourcePlan) return;

    let { WeeklySchedules } = sourcePlan;
    WeeklySchedules =
      WeeklySchedules
        ?.filter((ws) =>
          previewActive
            ? ws.status === "active"
            : ws.status === "pending" || ws.status === "activeCopy"
        )
        .sort((a, b) => a.weekIndex - b.weekIndex) || [];

    setWeeklySchedules(WeeklySchedules);

    const DAYS = ["su", "mo", "tu", "we", "th", "fr", "sa"];
    const allTasks = WeeklySchedules.flatMap((ws) =>
      (ws.tasks || []).map((task) => ({ ...task, weekIndex: ws.weekIndex }))
    );

    const sortedAllTasks = allTasks.sort((a, b) => {
      const aHasRemind = Number.isInteger(a.remindHour);
      const bHasRemind = Number.isInteger(b.remindHour);
      const totalDaysInPlan = (weeklySchedules.length || WeeklySchedules.length) * 7;

      const getAdjustedStart = (task) => {
        const startDayAbsolute = task.weekIndex * 7 + DAYS.indexOf(task.day);
        const endDayAbsolute = startDayAbsolute + task.duration;
        if (endDayAbsolute > totalDaysInPlan) {
          return { weekIndex: 0, dayIndex: 0 };
        }
        return {
          weekIndex: task.weekIndex,
          dayIndex: DAYS.indexOf(task.day),
        };
      };

      const aAdjusted = getAdjustedStart(a);
      const bAdjusted = getAdjustedStart(b);

      if (!aHasRemind && !bHasRemind) {
        if (aAdjusted.weekIndex !== bAdjusted.weekIndex) {
          return aAdjusted.weekIndex - bAdjusted.weekIndex;
        }
        if (aAdjusted.dayIndex !== bAdjusted.dayIndex) {
          return aAdjusted.dayIndex - bAdjusted.dayIndex;
        }
        return b.duration - a.duration;
      }
      if (!aHasRemind) return -1;
      if (!bHasRemind) return 1;
      return (a.remindHour - b.remindHour) || (a.remindMinute - b.remindMinute);
    });

    let weekTaskLevels = {};

    const tasksAllWeeks = sortedAllTasks.flatMap((task) => {
      const startWeek = Number.isInteger(task.weekIndex) ? task.weekIndex : 0;
      const startDayIndex = DAYS.indexOf(task.day);
      const totalDays = task.duration;
      const totalWeeks = weeklySchedules.length || WeeklySchedules.length;

      let tasksWithDays = [];
      let taskLevel = 0;
      let isConflict = true;

      while (isConflict) {
        isConflict = false;
        let occupiedLevels = new Set();

        for (let i = 0; i < totalDays; i++) {
          let currentDayIndex = (startDayIndex + i) % 7;
          let weekOffset = Math.floor((startDayIndex + i) / 7);
          let targetWeek = (startWeek + weekOffset) % totalWeeks;

          if (!weekTaskLevels[targetWeek]) weekTaskLevels[targetWeek] = {};
          if (!weekTaskLevels[targetWeek][currentDayIndex]) {
            weekTaskLevels[targetWeek][currentDayIndex] = [];
          }

          occupiedLevels = new Set([
            ...occupiedLevels,
            ...weekTaskLevels[targetWeek][currentDayIndex],
          ]);
        }

        if (occupiedLevels.has(taskLevel)) {
          taskLevel++;
          isConflict = true;
        }
      }

      for (let i = 0; i < totalDays; i++) {
        let currentDayIndex = (startDayIndex + i) % 7;
        let weekOffset = Math.floor((startDayIndex + i) / 7);
        let targetWeek =
          (startWeek + weekOffset) % (weeklySchedules.length || WeeklySchedules.length);

        if (!Number.isInteger(targetWeek) || !Number.isInteger(currentDayIndex)) {
          continue;
        }

        if (!weekTaskLevels[targetWeek]) weekTaskLevels[targetWeek] = {};
        if (!weekTaskLevels[targetWeek][currentDayIndex]) {
          weekTaskLevels[targetWeek][currentDayIndex] = [];
        }

        weekTaskLevels[targetWeek][currentDayIndex].push(taskLevel);

        const DAYS_LOCAL = DAYS;
        let existingTask = tasksWithDays.find(
          (t) =>
            t.id === task.id &&
            t.weekIndex === targetWeek &&
            t.day === DAYS_LOCAL[currentDayIndex]
        );

        if (existingTask) {
          existingTask.duration = totalDays;
        } else {
          const isContinued = startWeek !== targetWeek && i === 0;

          tasksWithDays.push({
            ...task,
            day: DAYS[currentDayIndex],
            weekIndex: targetWeek,
            position: taskLevel,
            originalDay: task.originalDay || task.day,
            originalWeek: task.originalWeek || task.weekIndex,
            duration: totalDays,
            isContinued,
          });
        }
      }

      return tasksWithDays;
    });

    setAllPlanTasks(tasksAllWeeks);

    const tasksForActiveWeek = tasksAllWeeks.filter((t) => t.weekIndex === activeWeek);
    setPlanTasks(tasksForActiveWeek.sort((a, b) => a.position - b.position));

    const { scenarios: scs } = WeeklySchedules?.[activeWeek] || {};
    setPlanScenarios(scs?.sort((a, b) => a.position - b.position));

    // Flat list of scenario activities across ALL weeks, used by the bulk
    // week-day picker to detect same-scenario + same-time occupancy.
    const allScenariosAllWeeks = WeeklySchedules.flatMap((ws) =>
      (ws.scenarios || []).map((s) => ({ ...s, weekIndex: ws.weekIndex }))
    );
    setAllPlanScenarios(allScenariosAllWeeks);

    const idsFromWeeks = WeeklySchedules?.[0]?.group || [];
    setPlanActiveCohortsIds(idsFromWeeks);

    setVersionStatus(
      previewActive
        ? "active"
        : WeeklySchedules.some((s) => s.status === "pending") ||
          plan.status === "pending"
        ? "pending"
        : "activeCopy"
    );

    setLoading(false);
  }, [plan, activeVersion, previewActive, activeWeek]);

  useEffect(() => {
    const from = previewActive && activeVersion ? activeVersion : plan;

    if (from?.DateSchedules?.length) {
      const allTriggeredTasks = from.DateSchedules.flatMap((ds) =>
        (ds.tasks || []).map((task) => ({ ...task, dateScheduleId: ds.id }))
      );
      setTriggeredTasks(allTriggeredTasks);
    } else {
      setTriggeredTasks([]);
    }
  }, [plan, activeVersion, previewActive]);

  const onBack = async () => {
    await refreshPlan?.();
    toList();
  };

  const addTaskToPlan = (task) => {
    setPlanTasks((planTasks) => [...planTasks, task]);
  };

  const addScenarioToPlan = (scenario) => {
    setPlanScenarios([...planScenarios, scenario]);
  };

  const updatePlanTitle = (e) => {
    if (readOnly) return;
    const { value } = e.target || {};
    setTitleValue(value);
    ApiPlanManagement.updatePlan(id, { title: value });
  };

  const updatePlanCohorts = (value) => {
    if (readOnly) return;
    const newCohortsList = value.map((v) => v.id);
    setPlanActiveCohortsIds(newCohortsList);

    // Track the in-flight cohort save so activation can wait for it and the
    // "no cohort" reminder reflects what is actually persisted.
    const savePromise = ApiPlanManagement.updatePlanCohort(plan.id, {
      groupScheduleIds: newCohortsList,
    }).finally(() => {
      refreshPlan();
    });
    // Keep the (possibly rejecting) promise so activation can detect a failed
    // cohort save; the extra no-op catch only suppresses the unhandled
    // rejection warning when the user never clicks Activate.
    savePromise.catch(() => {});
    cohortSaveRef.current = savePromise;
  };

  const updateTaskPlan = (plan) => {
    if (readOnly) return;
    setPlanTasks(plan);
  };

  const updateScenariosPlan = (plan) => {
    if (readOnly) return;
    setPlanScenarios(plan);
  };

  const discardPlan = async () => {
    if (readOnly) return;
    if (discardingRef.current) return;

    discardingRef.current = true;
    setDiscarding(true);

    try {
      await ApiPlanManagement.discardPlan(plan.id);
      await delay(2000);
      await refreshPlan();

      openNotification({
        type: "success",
        message: <p>{t("plan_management.notification.discard")}</p>,
      });

      setVersionStatus("activeCopy");
    } finally {
      await delay(1000);
      setDiscarding(false);
      discardingRef.current = false;
    }
  };

  const addWeek = () => {
    if (readOnly) return;
    ApiPlanManagement.addWeek(plan.id).then(() => {
      refreshPlan();
    });
  };

  const activatePlan = async () => {
    if (readOnly) return;
    if (activatingRef.current) return;

    activatingRef.current = true;
    setProcess(true);

    try {
      // Make sure any pending cohort change is persisted before activating,
      // so the reminder and the activated plan agree on the cohort state. If
      // the cohort save failed, stop here — proceeding could activate with a
      // stale cohort and hide the reminder.
      try {
        await cohortSaveRef.current;
      } catch (cohortError) {
        console.error(cohortError);
        // Clear the rejected promise so a retry isn't blocked by the same
        // stale failure after the plan state has refreshed.
        cohortSaveRef.current = Promise.resolve();
        openNotification({
          type: "error",
          message: <p>{t("plan_management.notification.error")}</p>,
        });
        return;
      }

      await ApiPlanManagement.activatePlan(plan.id);
      await delay(2500);

      openNotification({
        type: "success",
        message: <p>{t("plan_management.notification.activated")}</p>,
      });

      if (!planActiveCohortsIds?.length) {
        openNotification({
          type: "info",
          message: (
            <p>{t("plan_management.notification.cohort_not_assigned")}</p>
          ),
        });
      }

      await delay(1000);
      setVersionStatus("activeCopy");
      await refreshPlan();
    } catch (e) {
      if (e?.response?.data?.error === "GroupsAlreadyUsed") {
        const cohorts = e?.response?.data?.groupsAlreadyUsed
          .map((c) => c.EnrollmentFieldOption.value)
          .join(", ");
        const plansTitles = e?.response?.data?.plans.map((p) => p.title).join(", ");

        openNotification({
          type: "error",
          message: (
            <p>
              {t("plan_management.notification.errorGroups", {
                cohorts,
                plans: plansTitles,
              })}
            </p>
          ),
        });
      } else {
        console.error(e);
      }
    } finally {
      await delay(1000);
      setProcess(false);
      activatingRef.current = false;
    }
  };

  const openDiscardModal = () => {
    setOpenDiscardModal(true);
  };

  const openChangeLogsModal = () => {
    setOpenChangeLogsModal(true);
  };

  const closeChangeLogsModal = () => {
    setOpenChangeLogsModal(false);
  };

  const handleChangeLogsCancel = () => {
    if (readOnly) return;
    closeChangeLogsModal();
  };

  const closeDiscardModal = () => {
    if (readOnly) return;
    setOpenDiscardModal(false);
  };

  const handleDiscardOk = () => {
    if (readOnly) return;
    discardPlan();
    closeDiscardModal();
  };

  const handleDiscardCancel = () => {
    if (readOnly) return;
    closeDiscardModal();
  };

  const onChangeWeek = (key) => {
    if (readOnly) return;
    const selectedWeek = weeklySchedules.find((week) => week.id === key);
    const index = selectedWeek.weekIndex;
    setActiveWeek(index);
  };

  const onAddTab = () => {
    if (readOnly) return;
    addWeek();
  };

  const items: TabsProps["items"] = weeklySchedules.map((schedule) => ({
    key: schedule.id,
    label: `Week ${schedule.weekIndex + 1}`,
  }));

  const handleMoveLeftWeek = (id) => {
    if (readOnly) return;

    let weekIndex = items.findIndex((item) => item.key === id);
    if (weekIndex > 0) {
      weekIndex -= 1;
    }

    ApiPlanManagement.reorderWeek(id, weekIndex).then(() => {
      refreshPlan();
      setActiveWeek(weekIndex);
    });
  };

  const handleMoveRightWeek = (id) => {
    if (readOnly) return;

    let weekIndex = items.findIndex((item) => item.key === id);
    if (weekIndex < items.length - 1) {
      weekIndex += 1;
    }

    ApiPlanManagement.reorderWeek(id, weekIndex).then(() => {
      refreshPlan();
      setActiveWeek(weekIndex);
    });
  };

  const handleDuplicateWeek = (id) => {
    if (readOnly) return;
    ApiPlanManagement.duplicateWeek(id).then(() => {
      refreshPlan();
    });
  };

  const openRemoveModal = (id) => {
    if (readOnly) return;
    setWeekToRemove(id);
    setRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    if (readOnly) return;
    setRemoveModalOpen(false);
    setWeekToRemove(null);
  };

  const handleRemoveConfirm = () => {
    if (readOnly) return;

    const selectedWeek = weeklySchedules.find((week) => week.id === weekToRemove);
    const index = selectedWeek.weekIndex;

    if (index + 1 === weeklySchedules.length) {
      setActiveWeek(index - 1);
    }

    ApiPlanManagement.deleteWeek(weekToRemove).then(() => {
      refreshPlan();
      closeRemoveModal();
    });
  };

  const renderTabLabel = (label, id) => {
    const isOnlyOneWeek = weeklySchedules.length === 1;
    let index = weeklySchedules.findIndex((x) => x.id === id);
    const isFirst = index === 0;
    const isLast = index === weeklySchedules.length - 1;

    // antd 5 deprecates both halves of this: the `overlay` prop and a Menu
    // built from children. One list of items says the same thing.
    const menuItems = [
      {
        key: "moveLeft",
        label: t("plan_management.tabs.move_left"),
        disabled: isFirst,
        onClick: () => handleMoveLeftWeek(id),
      },
      {
        key: "moveRight",
        label: t("plan_management.tabs.move_right"),
        disabled: isLast,
        onClick: () => handleMoveRightWeek(id),
      },
      {
        key: "duplicate",
        label: t("plan_management.tabs.duplicate"),
        onClick: () => handleDuplicateWeek(id),
      },
      {
        key: "remove",
        label: t("plan_management.tabs.remove"),
        disabled: isOnlyOneWeek,
        onClick: () => openRemoveModal(id),
      },
    ];

    return (
      <span style={{ display: "flex", alignItems: "center" }}>
        {label}
        {!readOnly && (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <span
              style={{
                marginLeft: 8,
                fontSize: 20,
                fontWeight: "bold",
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ⋮
            </span>
          </Dropdown>
        )}
      </span>
    );
  };

  const tabItems = items.map((item) => ({
    ...item,
    label: renderTabLabel(item.label, item.key),
  }));

  const selectedWeekNumber = () => {
    const selectedWeek = weeklySchedules.find((week) => week.id === weekToRemove);
    const index = selectedWeek?.weekIndex;
    return index + 1;
  };

  const collectUniqueFieldsById = (items = []) => {
    const map = new Map();

    for (const item of items) {
      if (!item?.id) continue;
      if (!map.has(item.id)) {
        map.set(item.id, item);
      }
    }

    return Array.from(map.values());
  };

  const scheduleTriggerFields = useMemo(() => {
    const source = previewActive && activeVersion ? activeVersion : plan;

    const triggeredTasks =
      source?.DateSchedules?.flatMap((ds) => ds?.tasks || []) || [];

    const weeklyTasks =
      source?.WeeklySchedules?.flatMap((ws) => ws?.tasks || []) || [];

    const allTasks = [...triggeredTasks, ...weeklyTasks];

    return collectUniqueFieldsById(
      allTasks
        .filter((task) => task?.subjectProfileFieldId && task?.SubjectProfileField)
        .map((task) => ({
          id: task.SubjectProfileField.id,
          title: task.SubjectProfileField.title,
        }))
    );
  }, [plan, activeVersion, previewActive]);

  const notificationTriggerFields = useMemo(() => {
    const source = previewActive && activeVersion ? activeVersion : plan;

    const triggeredTasks =
      source?.DateSchedules?.flatMap((ds) => ds?.tasks || []) || [];

    const weeklyTasks =
      source?.WeeklySchedules?.flatMap((ws) => ws?.tasks || []) || [];

    const weeklyScenarios =
      source?.WeeklySchedules?.flatMap((ws) => ws?.scenarios || []) || [];

    const allItems = [...triggeredTasks, ...weeklyTasks, ...weeklyScenarios];

    return collectUniqueFieldsById(
      allItems
        .filter(
          (item) =>
            item?.notificationType === "relative" &&
            item?.triggerSubjectProfileFieldId &&
            item?.triggerSubjectProfileField
        )
        .map((item) => ({
          id: item.triggerSubjectProfileField.id,
          title: item.triggerSubjectProfileField.title,
        }))
    );
  }, [plan, activeVersion, previewActive]);

  const canOpenDefaultDataModal =
    scheduleTriggerFields.length > 0 || notificationTriggerFields.length > 0;

  const cohortOptions = useMemo(
    () => [...cohorts, ...normalizeGroups(sourcePlan?.activeGroups || [])],
    [cohorts, sourcePlan?.activeGroups]
  );

  const selectedPlanCohorts = useMemo(
    () =>
      cohortOptions.filter((cohort) =>
        planActiveCohortsIds?.includes(cohort.id)
      ),
    [cohortOptions, planActiveCohortsIds]
  );

  const planSettingsDropdown = (
    <div className="plan-settings-dropdown" onClick={(e) => e.stopPropagation()}>
      <div className="plan-settings-dropdown-field">
        <p className="plan-settings-dropdown-label">
          {t("plan_management.table.cohort")}
        </p>
        <TagsSelect
          key={`cohorts-${cohortsKey}`}
          defaultValue={selectedPlanCohorts}
          options={cohortOptions}
          onChange={updatePlanCohorts}
          selectPlaceholder="Select cohort"
          placeholder={
            <>
              <span className="plan-settings-add-plus">+</span> Add
            </>
          }
          readonly={readOnly}
        />
      </div>

      <div className="plan-settings-dropdown-field">
        <p className="plan-settings-dropdown-label">
          {t("plan_management.completion_logic")}
        </p>
        <FormSelect
          options={SELECT_COMPLETION_OPTIONS}
          className="plan-settings-dropdown-select"
          classNames={{ popup: { root: "plan-settings-dropdown-select-popup" } }}
          getPopupContainer={(trigger) =>
            trigger.closest(".plan-settings-dropdown") || document.body
          }
          placeholder="Select"
          value={
            SELECT_COMPLETION_OPTIONS.find((x) => x.key === completionLogic)
              ?.value
          }
          onChange={(selectedValue) => {
            if (readOnly) return;
            const selectedKey = SELECT_COMPLETION_OPTIONS.find(
              (option) => option.value === selectedValue
            )?.key;
            if (selectedKey) {
              updateCompletionLogic(selectedKey);
            }
          }}
          disabled={readOnly}
        />
      </div>
    </div>
  );

  const openDefaultDataModal = () => {
    if (readOnly) return;
    setDefaultDataModalOpen(true);
  };

  const closeDefaultDataModal = () => {
    setDefaultDataModalOpen(false);
  };

  const saveDefaultData = async (payload) => {
    if (readOnly) return;

    try {
      const normalizedPayload = normalizeDefaultData(payload);

      const response = await ApiPlanManagement.updatePlanDefaultData(
        plan.id,
        normalizedPayload
      );

      const savedDefaultData = normalizeDefaultData(
        response?.data?.defaultData || normalizedPayload
      );

      setPlanDefaultData(savedDefaultData);
      setDefaultDataModalOpen(false);

      await refreshPlan();

      openNotification?.({
        type: "success",
        message: <p>Default data updated</p>,
      });
    } catch (error) {
      console.error("Failed to update default data:", error);

      openNotification?.({
        type: "error",
        message: <p>Failed to update default data</p>,
      });
    }
  };

  return (
    <div className="plan-page">
      {isLoading ? (
        <div className="plan-page-loading-box">
          <Spin
            className="plan-page-loading"
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          />
        </div>
      ) : (
        <>
          <div className="plan-page-header">
            <div className="plan-page-header-right">
              <Button
                icon={<Icon name={"arrow-left"} />}
                style={{ marginRight: 16 }}
                onClick={onBack}
              />
              <div className="plan-page-title-container">
                <input
                  ref={titleRef}
                  type="text"
                  value={titleValue}
                  onChange={updatePlanTitle}
                  className="plan-page-name"
                  style={{
                    width: `${titleWidth}px`,
                    maxWidth: `${MAX_WIDTH}px`,
                    minWidth: `${MIN_WIDTH}px`,
                  }}
                  disabled={readOnly}
                />
              </div>

              <div className="plan-page-title-meta">
                <UserActivityStatus
                  userStatus={plan.status}
                  fontSize={14}
                  style={{ height: 32, alignItems: "center" }}
                />
                <Dropdown
                  trigger={["click"]}
                  placement="bottomLeft"
                  popupRender={() => planSettingsDropdown}
                  classNames={{ root: "plan-settings-dropdown-overlay" }}
                  styles={{ root: { marginLeft: -16 } }}
                >
                  <Button
                    type="text"
                    className="plan-page-settings-trigger"
                    disabled={readOnly}
                    aria-label="Plan settings"
                  >
                    ⋮
                  </Button>
                </Dropdown>
              </div>
            </div>

            {/* Mobile-only forced flex break — sits between ⋮ (row 1) and the
                status badge (row 2) so that status + action buttons start a
                fresh row aligned left. Hidden on desktop via CSS. */}
            <div className="plan-page-header-mobile-break" aria-hidden="true" />

            <div className="plan-page-header-left">
              {previewActive ? (
                <Button
                  type="primary"
                  onClick={handleReturnToDraft}
                  className="scenario-page-header-left-btn"
                >
                  {t("trails.return_to_draft")}
                </Button>
              ) : (
                <>
                  {canOpenDefaultDataModal && (
                    <Button
                      onClick={openDefaultDataModal}
                      className="plan-page-header-left-btn"
                      disabled={inProcess || discarding || readOnly}
                    >
                      Set default data
                    </Button>
                  )}

                  {versionStatus === "pending" && (
                    <>
                      <Button
                        onClick={openDiscardModal}
                        className="plan-page-header-left-btn"
                        loading={discarding}
                        disabled={inProcess || discarding}
                      >
                        {t("plan_management.discard_changes")}
                      </Button>

                      <Button
                        onClick={openChangeLogsModal}
                        className="plan-page-header-left-btn"
                        loading={discarding}
                        disabled={inProcess || discarding}
                      >
                        {t("plan_management.view_changes")} (
                        {changeLogs[plan.id]?.length || 0})
                      </Button>

                      <Button
                        type="primary"
                        onClick={activatePlan}
                        className="plan-page-header-left-btn"
                        loading={inProcess}
                        disabled={inProcess || discarding}
                      >
                        {plan.status === "active" || plan.status === "pending"
                          ? t("plan_management.save")
                          : t("plan_management.activate")}
                      </Button>
                    </>
                  )}
                </>
              )}

              <ChangeLogsModalComponent
                isModalOpen={isOpenChangeLogsModal}
                handleCancel={handleChangeLogsCancel}
                title={"Changes"}
                logs={logsForPlan}
                onPreviewActive={handlePreviewActive}
              />

              <DiscardModalComponent
                isModalOpen={isOpenDiscardModal}
                handleOk={handleDiscardOk}
                handleCancel={handleDiscardCancel}
                title={"Discard changes"}
                confirmLoading={discarding}
              />
            </div>
          </div>

          <TriggeredTasks
            tasks={triggeredTasks}
            planId={plan.id}
            refreshPlan={refreshPlan}
            readOnly={readOnly}
            activeGroupScheduleIds={planActiveCohortsIds}
          />

          <div>
            <Card>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Tabs
                  defaultActiveKey={activeWeek}
                  items={tabItems}
                  onChange={onChangeWeek}
                  style={{ flexGrow: 1 }}
                  tabBarExtraContent={{
                    left: (
                      <div
                        className="plan-page_block-title"
                        style={{ marginRight: 16 }}
                      >
                        {t("plan_management.weekly_plan")}
                      </div>
                    ),
                    right: readOnly ? null : (
                      <Button
                        type="text"
                        style={{
                          padding: "4px 12px",
                          color: "#595959",
                          fontSize: 14,
                        }}
                        onClick={onAddTab}
                      >
                        + Add
                      </Button>
                    ),
                  }}
                />
              </div>

              <PlanCalendar
                planId={plan.id}
                planTasks={planTasks}
                addTaskToPlan={addTaskToPlan}
                updateTaskPlan={updateTaskPlan}
                planScenarios={planScenarios}
                scenariosList={scenarios}
                addScenarioToPlan={addScenarioToPlan}
                updateScenariosPlan={updateScenariosPlan}
                refreshPlan={refreshPlan}
                weekIndex={activeWeek}
                weeklySchedules={weeklySchedules}
                readOnly={readOnly}
                activeGroupScheduleIds={planActiveCohortsIds}
                allPlanTasks={allPlanTasks}
                allPlanScenarios={allPlanScenarios}
                planDefaultData={planDefaultData}
                onWeekDayPatch={async (patch) => {
                  if (readOnly) return;

                  setProcess(true);
                  try {
                    await ApiPlanManagement.applyWeekDayPatch(plan.id, patch);
                    await refreshPlan();
                  } catch (e) {
                    console.error(e);
                    openNotification?.({
                      type: "error",
                      message: <p>Failed to apply changes</p>,
                    });
                    throw e;
                  } finally {
                    setProcess(false);
                  }
                }}
              />
            </Card>
          </div>

          <RemoveModalComponent
            isModalOpen={isRemoveModalOpen}
            handleOk={handleRemoveConfirm}
            handleCancel={closeRemoveModal}
            title={`Are you sure you want to remove the "Week ${selectedWeekNumber()}"?`}
          />

          <PlanDefaultDataModal
            open={isDefaultDataModalOpen}
            onCancel={closeDefaultDataModal}
            onSave={saveDefaultData}
            scheduleFields={scheduleTriggerFields}
            notificationFields={notificationTriggerFields}
            initialValues={planDefaultData}
            readOnly={readOnly}
          />
        </>
      )}
    </div>
  );
};

export default PlanPage;
